/**
 * IT Ticketing data service (Supabase, migration 00550_it_ticketing_tickets_comments).
 *
 * The single read/write path for tickets and their comment threads. Nothing
 * outside this file touches an `it_ticket*` table or sees a snake_case
 * column: every row goes through a private `rowTo*` mapper on the way out,
 * and every write builds its own snake_case payload on the way in. Mirrors
 * the conventions in modules/crm/services/crmService.ts.
 *
 * Org scoping: every row carries `org_id`, resolved from this module's OWN
 * customer-context state (lib/ticketingCustomerContext.ts), not the app-wide
 * PPM portfolio picker — see that file's doc comment for why the two are kept
 * deliberately separate. itTicketingOrgId() always resolves to a real
 * customer id (never the "all customers" sentinel), so reads and writes can
 * never disagree on scope; itTicketingOrgIdForRead() is the one place that
 * sentinel is allowed to mean "don't filter" (see listTickets()).
 *
 * `ticket_number` is never sent on create: the database assigns it (see the
 * migration's BEFORE INSERT trigger), so it's absent from `TicketInput`.
 */
import { supabase } from '@/lib/supabase'
import { getActiveTicketingCustomerId, ALL_CUSTOMERS_SENTINEL } from '../lib/ticketingCustomerContext'
import { generateId } from '@/lib/utils'
import { uploadObject, removeObject, signedUrl } from '@/lib/objectStorage'
import { parseAssessment } from '../lib/priorityAssessment'
import {
  TICKET_STATUSES, TICKET_PRIORITIES, TICKET_FIELD_TYPES, TICKETING_ROLES, APPROVAL_STATUSES,
  type Ticket, type TicketComment, type TicketInput, type NewTicketInput, type TicketStatus, type TicketPriority,
  type TicketStateEvent, type TicketFieldDef, type TicketFieldDefInput, type TicketFieldType,
  type TicketingRole, type TicketRolePermission, type TicketWorkflowRule, type TicketWorkflowRuleInput,
  type ApproverType, type TicketApproval, type ApprovalStatus,
  type TicketDelegation, type TicketDelegationInput,
  type TicketAttachment, type TicketAuditEntry, type TicketingCustomer,
  REPORT_CARD_KINDS, REPORT_CARD_MEASURES, REPORT_CHART_TYPES, isReportGroupKey,
  type TicketReportCard, type TicketReportCardInput, type ReportCardKind, type ReportCardMeasure,
  type ReportChartType, type TicketSavedView, type TicketSavedViewInput,
  type TicketSavedViewFilters, type TicketCategory, type TicketTask, type TicketTaskInput, type TicketNumberSettings, type TicketingCustomerMember,
} from '../types/ticket'

const ATTACHMENTS_CONTAINER = 'ticket-attachments' as const

export const DEFAULT_IT_TICKETING_ORG_ID = 'org_demo'

/**
 * The customer id to stamp on a WRITE (create/update a ticket, workflow rule,
 * field def, report card, saved view, ...) and to use as an equality filter
 * everywhere except the one read path that needs to support "no filter".
 *
 * Never returns ALL_CUSTOMERS_SENTINEL: an admin who has picked "All
 * customers" still needs a concrete customer id the moment they write
 * something (you can't file a ticket under "every customer at once"), so the
 * sentinel falls back to the module default here instead of leaking into a
 * row's org_id.
 */
export function itTicketingOrgId(): string {
  const active = getActiveTicketingCustomerId()
  if (!active || active === ALL_CUSTOMERS_SENTINEL) return DEFAULT_IT_TICKETING_ORG_ID
  return active
}

/**
 * The customer id to filter a READ by, or null to mean "don't filter" — used
 * only by listTickets()'s org_id filter, so an admin who picked "All
 * customers" sees every customer's tickets via RLS rather than being
 * silently narrowed back to the default.
 */
export function itTicketingOrgIdForRead(): string | null {
  const active = getActiveTicketingCustomerId()
  if (active === ALL_CUSTOMERS_SENTINEL) return null
  return active ?? DEFAULT_IT_TICKETING_ORG_ID
}

function s(v: unknown): string {
  return v == null ? '' : String(v)
}

function sOrNull(v: unknown): string | null {
  if (v == null) return null
  const t = String(v)
  return t.length > 0 ? t : null
}

function oneOf<T extends string>(v: unknown, allowed: readonly T[], fallback: T): T {
  const t = v == null ? '' : String(v)
  return (allowed as readonly string[]).includes(t) ? (t as T) : fallback
}

function compact(row: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(row)) if (v !== undefined) out[k] = v
  return out
}

function jsonObject(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}
}

function strArray(v: unknown): string[] {
  if (Array.isArray(v)) return v.map((x) => String(x)).filter(Boolean)
  return []
}

function rowToTicket(row: Record<string, unknown>): Ticket {
  return {
    id: s(row.id),
    orgId: sOrNull(row.org_id),
    ticketNumber: s(row.ticket_number),
    title: s(row.title),
    description: s(row.description),
    status: oneOf<TicketStatus>(row.status, TICKET_STATUSES, 'New'),
    priority: oneOf<TicketPriority>(row.priority, TICKET_PRIORITIES, 'Medium'),
    category: s(row.category),
    dueDate: sOrNull(row.due_date),
    resolvedAt: sOrNull(row.resolved_at),
    requesterId: s(row.requester_id),
    createdBy: sOrNull(row.created_by),
    assigneeId: sOrNull(row.assignee_id),
    attrs: jsonObject(row.attrs),
    priorityAssessment: parseAssessment(row.priority_assessment),
    createdAt: s(row.created_at),
    updatedAt: s(row.updated_at),
  }
}

function rowToFieldDef(row: Record<string, unknown>): TicketFieldDef {
  return {
    id: s(row.id),
    orgId: sOrNull(row.org_id),
    key: s(row.key),
    label: s(row.label),
    fieldType: oneOf<TicketFieldType>(row.field_type, TICKET_FIELD_TYPES, 'text'),
    options: strArray(row.options),
    required: row.required === true,
    section: sOrNull(row.section),
    seq: typeof row.seq === 'number' ? row.seq : 0,
    helpText: sOrNull(row.help_text),
    active: row.active !== false,
    createdBy: sOrNull(row.created_by),
    createdAt: s(row.created_at),
    updatedAt: s(row.updated_at),
  }
}

function rowToComment(row: Record<string, unknown>): TicketComment {
  return {
    id: s(row.id),
    orgId: sOrNull(row.org_id),
    ticketId: s(row.ticket_id),
    authorId: s(row.author_id),
    body: s(row.body),
    createdAt: s(row.created_at),
  }
}

function rowToStateEvent(row: Record<string, unknown>): TicketStateEvent {
  return {
    id: s(row.id),
    ticketId: s(row.ticket_id),
    orgId: sOrNull(row.org_id),
    status: oneOf<TicketStatus>(row.status, TICKET_STATUSES, 'New'),
    assigneeId: sOrNull(row.assignee_id),
    changedBy: sOrNull(row.changed_by),
    startedAt: s(row.started_at),
  }
}

function ticketPayload(input: Partial<TicketInput>): Record<string, unknown> {
  return compact({
    title: input.title,
    description: input.description,
    category: input.category,
    priority: input.priority,
    status: input.status,
    requester_id: input.requesterId,
    assignee_id: input.assigneeId,
    due_date: input.dueDate,
    attrs: input.attrs,
    priority_assessment: input.priorityAssessment,
  })
}

function fieldDefPayload(input: Partial<TicketFieldDefInput>): Record<string, unknown> {
  return compact({
    key: input.key,
    label: input.label,
    field_type: input.fieldType,
    options: input.options,
    required: input.required,
    section: input.section,
    seq: input.seq,
    help_text: input.helpText,
  })
}

// ---------------------------------------------------------------------------
// Customers + agent membership (00563) — the customer picker's data layer.
// it_ticketing_customers is admin-write / authenticated-read; it_ticketing_agents
// is the same (see 00547), so every write here relies on RLS to reject a
// non-admin rather than re-checking role client-side.
// ---------------------------------------------------------------------------

function rowToCustomer(row: Record<string, unknown>): TicketingCustomer {
  return {
    id: s(row.id),
    name: s(row.name),
    active: row.active !== false,
  }
}

export async function listTicketingCustomers(): Promise<TicketingCustomer[]> {
  const { data, error } = await supabase.from('it_ticketing_customers').select('*').order('name', { ascending: true })
  if (error) throw new Error(`Supabase query error [it_ticketing_customers]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToCustomer)
}

export async function createTicketingCustomer(name: string): Promise<TicketingCustomer> {
  const row = { id: `cust_${generateId()}`, name, active: true }
  const { data, error } = await supabase.from('it_ticketing_customers').insert(row).select().single()
  if (error) throw new Error(`Supabase insert error [it_ticketing_customers]: ${error.message}`)
  return rowToCustomer(data as Record<string, unknown>)
}

export async function renameTicketingCustomer(id: string, name: string): Promise<void> {
  const { error } = await supabase.from('it_ticketing_customers').update({ name }).eq('id', id)
  if (error) throw new Error(`Supabase update error [it_ticketing_customers]: ${error.message}`)
}

export async function setTicketingCustomerActive(id: string, active: boolean): Promise<void> {
  const { error } = await supabase.from('it_ticketing_customers').update({ active }).eq('id', id)
  if (error) throw new Error(`Supabase update error [it_ticketing_customers]: ${error.message}`)
}

// The customer ids the signed-in user is an active agent for. currentUserId
// is passed in by the calling page (from useAppStore's currentUser), same as
// every other "who am I" call site in this file (see createDelegation,
// createReportCard, createSavedView) — there's no server-side "current user"
// resolution here, it's always handed down from the caller.
export async function listMyActiveAgentOrgIds(currentUserId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('it_ticketing_agents')
    .select('org_id')
    .eq('user_id', currentUserId)
    .eq('active', true)
  if (error) throw new Error(`Supabase query error [it_ticketing_agents]: ${error.message}`)
  const ids = ((data ?? []) as Record<string, unknown>[]).map((r) => sOrNull(r.org_id)).filter((id): id is string => id != null)
  return [...new Set(ids)]
}

export async function listAgentsForCustomer(orgId: string): Promise<Array<{ id: string; userId: string; userName: string; active: boolean }>> {
  const { data, error } = await supabase
    .from('it_ticketing_agents')
    .select('id, user_id, active')
    .eq('org_id', orgId)
    .order('created_at', { ascending: true })
  if (error) throw new Error(`Supabase query error [it_ticketing_agents]: ${error.message}`)
  const rows = (data ?? []) as Record<string, unknown>[]
  const userIds = [...new Set(rows.map((r) => s(r.user_id)).filter(Boolean))]
  let nameById = new Map<string, string>()
  if (userIds.length > 0) {
    const { data: users, error: usersError } = await supabase.from('users').select('id, name').in('id', userIds)
    if (usersError) throw new Error(`Supabase query error [users]: ${usersError.message}`)
    nameById = new Map(((users ?? []) as Record<string, unknown>[]).map((u) => [s(u.id), s(u.name)]))
  }
  return rows.map((r) => {
    const userId = s(r.user_id)
    return {
      id: s(r.id),
      userId,
      userName: nameById.get(userId) ?? 'Unknown',
      active: r.active !== false,
    }
  })
}

export async function addTicketingAgent(orgId: string, userId: string): Promise<void> {
  // Re-adding a previously-removed agent reactivates them instead of erroring
  // on the (coalesce(org_id,''), user_id) unique index (00547). That index is
  // a functional (coalesce) index, which Postgres' ON CONFLICT target can't
  // match against a plain (org_id, user_id) column list, so this checks for
  // an existing row itself rather than relying on upsert's onConflict.
  const { data: existing, error: findError } = await supabase
    .from('it_ticketing_agents')
    .select('id')
    .eq('org_id', orgId)
    .eq('user_id', userId)
    .maybeSingle()
  if (findError) throw new Error(`Supabase query error [it_ticketing_agents]: ${findError.message}`)
  if (existing) {
    const { error } = await supabase.from('it_ticketing_agents').update({ active: true }).eq('id', s(existing.id))
    if (error) throw new Error(`Supabase update error [it_ticketing_agents]: ${error.message}`)
    return
  }
  const row = { id: `ita_${generateId()}`, org_id: orgId, user_id: userId, active: true }
  const { error } = await supabase.from('it_ticketing_agents').insert(row)
  if (error) throw new Error(`Supabase insert error [it_ticketing_agents]: ${error.message}`)
}

export async function setTicketingAgentActive(agentRowId: string, active: boolean): Promise<void> {
  const { error } = await supabase.from('it_ticketing_agents').update({ active }).eq('id', agentRowId)
  if (error) throw new Error(`Supabase update error [it_ticketing_agents]: ${error.message}`)
}

export async function listTickets(): Promise<Ticket[]> {
  let q = supabase.from('it_tickets').select('*')
  const orgId = itTicketingOrgIdForRead()
  if (orgId != null) q = q.eq('org_id', orgId)
  const { data, error } = await q.order('created_at', { ascending: false })
  if (error) throw new Error(`Supabase query error [it_tickets]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToTicket)
}

export async function getTicket(id: string): Promise<Ticket | null> {
  const { data, error } = await supabase.from('it_tickets').select('*').eq('id', id).maybeSingle()
  if (error) throw new Error(`Supabase query error [it_tickets]: ${error.message}`)
  return data ? rowToTicket(data as Record<string, unknown>) : null
}

export async function createTicket(input: NewTicketInput): Promise<Ticket> {
  const row = {
    id: `it_tkt_${generateId()}`,
    // The form's chosen customer, else the active one. RLS (00568) still
    // checks the filer may create under it.
    org_id: input.orgId || itTicketingOrgId(),
    ...ticketPayload(input),
  }
  const { data, error } = await supabase.from('it_tickets').insert(row).select().single()
  if (error) throw new Error(`Supabase insert error [it_tickets]: ${error.message}`)
  return rowToTicket(data as Record<string, unknown>)
}

export async function updateTicket(id: string, patch: Partial<TicketInput>): Promise<Ticket> {
  const { data, error } = await supabase
    .from('it_tickets')
    .update(ticketPayload(patch))
    .eq('id', id)
    .select()
    .single()
  if (error) throw new Error(`Supabase update error [it_tickets]: ${error.message}`)
  return rowToTicket(data as Record<string, unknown>)
}

/**
 * Apply the same patch to many tickets (bulk edit). One UPDATE per chunk of
 * ids, so the per-row triggers (audit, state history, approval sync) still
 * run for every ticket. RLS quietly skips any ticket this user may not edit,
 * so those come back in `skippedIds` rather than as an error.
 */
export async function bulkUpdateTickets(
  ids: string[],
  patch: Partial<Pick<TicketInput, 'status' | 'priority' | 'category' | 'assigneeId' | 'dueDate'>>,
): Promise<{ updated: Ticket[]; skippedIds: string[] }> {
  const payload = ticketPayload(patch)
  if (Object.keys(payload).length === 0) throw new Error('Choose at least one field to change.')
  const updated: Ticket[] = []
  for (let i = 0; i < ids.length; i += 100) {
    const chunk = ids.slice(i, i + 100)
    const { data, error } = await supabase.from('it_tickets').update(payload).in('id', chunk).select()
    if (error) throw new Error(`Supabase update error [it_tickets]: ${error.message}`)
    updated.push(...((data ?? []) as Record<string, unknown>[]).map(rowToTicket))
  }
  const done = new Set(updated.map((t) => t.id))
  return { updated, skippedIds: ids.filter((id) => !done.has(id)) }
}

export async function deleteTicket(id: string): Promise<void> {
  const { error } = await supabase.from('it_tickets').delete().eq('id', id)
  if (error) throw new Error(`Supabase delete error [it_tickets]: ${error.message}`)
}

export async function listComments(ticketId: string): Promise<TicketComment[]> {
  const { data, error } = await supabase
    .from('it_ticket_comments')
    .select('*')
    .eq('ticket_id', ticketId)
    .order('created_at', { ascending: true })
  if (error) throw new Error(`Supabase query error [it_ticket_comments]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToComment)
}

export async function addComment(input: { ticketId: string; authorId: string; body: string }): Promise<TicketComment> {
  const row = {
    id: `it_cmt_${generateId()}`,
    org_id: itTicketingOrgId(),
    ticket_id: input.ticketId,
    author_id: input.authorId,
    body: input.body,
  }
  const { data, error } = await supabase.from('it_ticket_comments').insert(row).select().single()
  if (error) throw new Error(`Supabase insert error [it_ticket_comments]: ${error.message}`)
  return rowToComment(data as Record<string, unknown>)
}

// State history is written only by the DB trigger (00553) — read-only here.
export async function listTicketStateHistory(ticketId: string): Promise<TicketStateEvent[]> {
  const { data, error } = await supabase
    .from('it_ticket_state_history')
    .select('*')
    .eq('ticket_id', ticketId)
    .order('started_at', { ascending: true })
  if (error) throw new Error(`Supabase query error [it_ticket_state_history]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToStateEvent)
}

// For reporting across many tickets at once (time-tracking report page).
export async function listAllStateHistory(ticketIds: string[]): Promise<TicketStateEvent[]> {
  if (ticketIds.length === 0) return []
  const { data, error } = await supabase
    .from('it_ticket_state_history')
    .select('*')
    .in('ticket_id', ticketIds)
    .order('started_at', { ascending: true })
  if (error) throw new Error(`Supabase query error [it_ticket_state_history]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToStateEvent)
}

// Custom ticket field definitions (00558) — admin-write, authenticated-read.
// Only active defs are returned for rendering the ticket form; the admin
// management page passes includeInactive to also show retired ones.
export async function listTicketFieldDefs(opts?: { includeInactive?: boolean }): Promise<TicketFieldDef[]> {
  let q = supabase.from('it_ticket_field_defs').select('*').eq('org_id', itTicketingOrgId()).order('seq', { ascending: true })
  if (!opts?.includeInactive) q = q.eq('active', true)
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [it_ticket_field_defs]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToFieldDef)
}

export async function createTicketFieldDef(input: TicketFieldDefInput): Promise<TicketFieldDef> {
  const row = {
    id: `it_fld_${generateId()}`,
    org_id: itTicketingOrgId(),
    ...fieldDefPayload(input),
  }
  const { data, error } = await supabase.from('it_ticket_field_defs').insert(row).select().single()
  if (error) throw new Error(`Supabase insert error [it_ticket_field_defs]: ${error.message}`)
  return rowToFieldDef(data as Record<string, unknown>)
}

export async function updateTicketFieldDef(id: string, patch: Partial<TicketFieldDefInput>): Promise<TicketFieldDef> {
  const { data, error } = await supabase
    .from('it_ticket_field_defs')
    .update(fieldDefPayload(patch))
    .eq('id', id)
    .select()
    .single()
  if (error) throw new Error(`Supabase update error [it_ticket_field_defs]: ${error.message}`)
  return rowToFieldDef(data as Record<string, unknown>)
}

// No hard delete — a retired field's defs stay so historical ticket attrs
// keep their label/type, same as CRM's "active" soft-delete convention.
export async function setTicketFieldDefActive(id: string, active: boolean): Promise<TicketFieldDef> {
  const { data, error } = await supabase
    .from('it_ticket_field_defs')
    .update({ active })
    .eq('id', id)
    .select()
    .single()
  if (error) throw new Error(`Supabase update error [it_ticket_field_defs]: ${error.message}`)
  return rowToFieldDef(data as Record<string, unknown>)
}

// --- RBAC + workflow (00559) -------------------------------------------------

function rowToRolePermission(row: Record<string, unknown>): TicketRolePermission {
  return {
    id: s(row.id),
    orgId: sOrNull(row.org_id),
    role: oneOf<TicketingRole>(row.role, TICKETING_ROLES, 'requester'),
    permission: s(row.permission),
    granted: row.granted === true,
  }
}

// Every role/permission grant, global (org_id null) for now — matches this
// module's single-org, demo-style scoping. Admin-write, authenticated-read.
export async function listRolePermissions(): Promise<TicketRolePermission[]> {
  const { data, error } = await supabase.from('it_ticketing_role_permissions').select('*')
  if (error) throw new Error(`Supabase query error [it_ticketing_role_permissions]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToRolePermission)
}

export async function setRolePermission(role: TicketingRole, permission: string, granted: boolean): Promise<void> {
  const { error } = await supabase
    .from('it_ticketing_role_permissions')
    .upsert({ id: `it_rp_${role}_${permission}`, org_id: null, role, permission, granted }, { onConflict: 'id' })
  if (error) throw new Error(`Supabase upsert error [it_ticketing_role_permissions]: ${error.message}`)
}

// My own resolved ticketing role (admin/agent/requester) and a permission
// check — both backed by the SQL functions in 00559 so the answer always
// matches what RLS itself would decide, not a client-side guess.
export async function myTicketingRole(): Promise<TicketingRole> {
  const { data, error } = await supabase.rpc('it_ticketing_my_role')
  if (error) throw new Error(`Supabase RPC error [it_ticketing_my_role]: ${error.message}`)
  return (TICKETING_ROLES as readonly string[]).includes(data) ? (data as TicketingRole) : 'requester'
}

export async function hasTicketingPermission(permission: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('it_ticketing_has_permission', { p_permission: permission })
  if (error) throw new Error(`Supabase RPC error [it_ticketing_has_permission]: ${error.message}`)
  return data === true
}

// --- Ticketing admins (00589) ---------------------------------------------------
// The hand-picked list that may change anything on the Admin page. Tesseract
// admins are always in; only they can add or remove people from the list.

export interface TicketingAdmin {
  userId: string
  addedBy: string | null
  createdAt: string | null
}

export async function isTicketingAdmin(): Promise<boolean> {
  const { data, error } = await supabase.rpc('it_ticketing_is_admin')
  if (error) throw new Error(`Supabase RPC error [it_ticketing_is_admin]: ${error.message}`)
  return data === true
}

export async function listTicketingAdmins(): Promise<TicketingAdmin[]> {
  const { data, error } = await supabase.from('it_ticketing_admins').select('*').order('created_at')
  if (error) throw new Error(`Supabase query error [it_ticketing_admins]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map((row) => ({
    userId: s(row.user_id),
    addedBy: sOrNull(row.added_by),
    createdAt: sOrNull(row.created_at),
  }))
}

export async function addTicketingAdmin(userId: string, addedBy: string): Promise<void> {
  const { error } = await supabase.from('it_ticketing_admins').upsert({ user_id: userId, added_by: addedBy }, { onConflict: 'user_id', ignoreDuplicates: true })
  if (error) throw new Error(`Supabase insert error [it_ticketing_admins]: ${error.message}`)
}

export async function removeTicketingAdmin(userId: string): Promise<void> {
  const { error } = await supabase.from('it_ticketing_admins').delete().eq('user_id', userId)
  if (error) throw new Error(`Supabase delete error [it_ticketing_admins]: ${error.message}`)
}

function rowToWorkflowRule(row: Record<string, unknown>): TicketWorkflowRule {
  return {
    id: s(row.id),
    orgId: sOrNull(row.org_id),
    name: s(row.name),
    fieldKey: s(row.field_key),
    value: s(row.value),
    approverType: (row.approver_type === 'role' ? 'role' : 'user') as ApproverType,
    approverUserId: sOrNull(row.approver_user_id),
    approverRole: row.approver_role == null ? null : oneOf<TicketingRole>(row.approver_role, TICKETING_ROLES, 'admin'),
    active: row.active !== false,
    createdBy: sOrNull(row.created_by),
    createdAt: s(row.created_at),
    updatedAt: s(row.updated_at),
  }
}

function workflowRulePayload(input: Partial<TicketWorkflowRuleInput>): Record<string, unknown> {
  return compact({
    name: input.name,
    field_key: input.fieldKey,
    value: input.value,
    approver_type: input.approverType,
    approver_user_id: input.approverType === 'role' ? null : input.approverUserId,
    approver_role: input.approverType === 'user' ? null : input.approverRole,
    active: input.active,
  })
}

export async function listWorkflowRules(): Promise<TicketWorkflowRule[]> {
  const { data, error } = await supabase
    .from('it_workflow_rules')
    .select('*')
    .eq('org_id', itTicketingOrgId())
    .order('created_at', { ascending: false })
  if (error) throw new Error(`Supabase query error [it_workflow_rules]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToWorkflowRule)
}

export async function createWorkflowRule(input: TicketWorkflowRuleInput): Promise<TicketWorkflowRule> {
  const row = { id: `it_rule_${generateId()}`, org_id: itTicketingOrgId(), ...workflowRulePayload(input) }
  const { data, error } = await supabase.from('it_workflow_rules').insert(row).select().single()
  if (error) throw new Error(`Supabase insert error [it_workflow_rules]: ${error.message}`)
  return rowToWorkflowRule(data as Record<string, unknown>)
}

export async function updateWorkflowRule(id: string, patch: Partial<TicketWorkflowRuleInput>): Promise<TicketWorkflowRule> {
  const { data, error } = await supabase.from('it_workflow_rules').update(workflowRulePayload(patch)).eq('id', id).select().single()
  if (error) throw new Error(`Supabase update error [it_workflow_rules]: ${error.message}`)
  return rowToWorkflowRule(data as Record<string, unknown>)
}

export async function deleteWorkflowRule(id: string): Promise<void> {
  const { error } = await supabase.from('it_workflow_rules').delete().eq('id', id)
  if (error) throw new Error(`Supabase delete error [it_workflow_rules]: ${error.message}`)
}

function rowToApproval(row: Record<string, unknown>): TicketApproval {
  return {
    id: s(row.id),
    orgId: sOrNull(row.org_id),
    ticketId: s(row.ticket_id),
    ruleId: s(row.rule_id),
    status: oneOf<ApprovalStatus>(row.status, APPROVAL_STATUSES, 'pending'),
    comment: sOrNull(row.comment),
    decidedAt: sOrNull(row.decided_at),
    decidedBy: sOrNull(row.decided_by),
    createdAt: s(row.created_at),
  }
}

// Written only by the it_tickets_sync_approvals trigger (00559) — read/decide here.
// For bulk edit: approvals across many tickets, to spot the ones a bulk
// change just created. Chunked so a large selection doesn't overflow the URL.
export async function listApprovalsForTickets(ticketIds: string[]): Promise<TicketApproval[]> {
  const out: TicketApproval[] = []
  for (let i = 0; i < ticketIds.length; i += 150) {
    const { data, error } = await supabase
      .from('it_ticket_approvals')
      .select('*')
      .in('ticket_id', ticketIds.slice(i, i + 150))
    if (error) throw new Error(`Supabase query error [it_ticket_approvals]: ${error.message}`)
    out.push(...((data ?? []) as Record<string, unknown>[]).map(rowToApproval))
  }
  return out
}

export async function listTicketApprovals(ticketId: string): Promise<TicketApproval[]> {
  const { data, error } = await supabase
    .from('it_ticket_approvals')
    .select('*')
    .eq('ticket_id', ticketId)
    .order('created_at', { ascending: true })
  if (error) throw new Error(`Supabase query error [it_ticket_approvals]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToApproval)
}

export async function decideApproval(id: string, decision: {
  status: Extract<ApprovalStatus, 'approved' | 'rejected'>
  comment?: string | null
  decidedBy: string
}): Promise<TicketApproval> {
  const { data, error } = await supabase
    .from('it_ticket_approvals')
    .update({ status: decision.status, comment: decision.comment ?? null, decided_at: new Date().toISOString(), decided_by: decision.decidedBy })
    .eq('id', id)
    .select()
    .single()
  if (error) throw new Error(`Supabase update error [it_ticket_approvals]: ${error.message}`)
  return rowToApproval(data as Record<string, unknown>)
}

function rowToDelegation(row: Record<string, unknown>): TicketDelegation {
  return {
    id: s(row.id),
    orgId: sOrNull(row.org_id),
    fromUserId: s(row.from_user_id),
    toUserId: s(row.to_user_id),
    endsAt: sOrNull(row.ends_at),
    active: row.active !== false,
    createdBy: sOrNull(row.created_by),
    createdAt: s(row.created_at),
    updatedAt: s(row.updated_at),
  }
}

export async function listDelegations(): Promise<TicketDelegation[]> {
  const { data, error } = await supabase
    .from('it_ticketing_delegations')
    .select('*')
    .eq('org_id', itTicketingOrgId())
    .order('created_at', { ascending: false })
  if (error) throw new Error(`Supabase query error [it_ticketing_delegations]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToDelegation)
}

export async function createDelegation(input: TicketDelegationInput, createdBy: string): Promise<TicketDelegation> {
  const row = compact({
    id: `it_del_${generateId()}`,
    org_id: itTicketingOrgId(),
    from_user_id: input.fromUserId,
    to_user_id: input.toUserId,
    ends_at: input.endsAt,
    active: input.active,
    created_by: createdBy,
  })
  const { data, error } = await supabase.from('it_ticketing_delegations').insert(row).select().single()
  if (error) throw new Error(`Supabase insert error [it_ticketing_delegations]: ${error.message}`)
  return rowToDelegation(data as Record<string, unknown>)
}

export async function setDelegationActive(id: string, active: boolean): Promise<TicketDelegation> {
  const { data, error } = await supabase.from('it_ticketing_delegations').update({ active }).eq('id', id).select().single()
  if (error) throw new Error(`Supabase update error [it_ticketing_delegations]: ${error.message}`)
  return rowToDelegation(data as Record<string, unknown>)
}

// ---------------------------------------------------------------------------
// Attachments (00560) — bytes in Azure Blob (container 'ticket-attachments'),
// this table is metadata only. Mirrors services/defectAttachmentService.ts:
// upload the object first, insert the metadata row second, and roll the
// object back if the insert fails so a failed write never leaves an orphaned blob.
// ---------------------------------------------------------------------------

/** A storage-safe file name that still reads like the original. */
function safeFileName(name: string): string {
  const cleaned = name.normalize('NFKD').replace(/[^\w.\- ]+/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^[-.]+/, '')
  return (cleaned || 'file').slice(0, 120)
}

function rowToAttachment(row: Record<string, unknown>): TicketAttachment {
  return {
    id: s(row.id),
    orgId: sOrNull(row.org_id),
    ticketId: s(row.ticket_id),
    fileName: s(row.file_name),
    mimeType: sOrNull(row.mime_type),
    sizeBytes: typeof row.size_bytes === 'number' ? row.size_bytes : null,
    storagePath: s(row.storage_path),
    uploadedBy: sOrNull(row.uploaded_by),
    createdAt: s(row.created_at),
  }
}

export async function listAttachments(ticketId: string): Promise<TicketAttachment[]> {
  const { data, error } = await supabase
    .from('it_ticket_attachments')
    .select('*')
    .eq('ticket_id', ticketId)
    .order('created_at', { ascending: true })
  if (error) throw new Error(`Supabase query error [it_ticket_attachments]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToAttachment)
}

export async function uploadAttachment(input: { ticketId: string; file: File; uploadedBy: string }): Promise<TicketAttachment> {
  const id = `it_att_${generateId()}`
  const path = `${itTicketingOrgId()}/${input.ticketId}/${id}-${safeFileName(input.file.name)}`
  await uploadObject(ATTACHMENTS_CONTAINER, path, input.file, { contentType: input.file.type || undefined })

  const row = {
    id,
    org_id: itTicketingOrgId(),
    ticket_id: input.ticketId,
    file_name: input.file.name,
    mime_type: input.file.type || null,
    size_bytes: input.file.size,
    storage_path: path,
    uploaded_by: input.uploadedBy,
  }
  const { data, error } = await supabase.from('it_ticket_attachments').insert(row).select().single()
  if (error) {
    await removeObject(ATTACHMENTS_CONTAINER, path).catch(() => {})
    throw new Error(`Supabase insert error [it_ticket_attachments]: ${error.message}`)
  }
  return rowToAttachment(data as Record<string, unknown>)
}

export async function attachmentUrl(a: TicketAttachment): Promise<string> {
  return signedUrl(ATTACHMENTS_CONTAINER, a.storagePath, 3600)
}

export async function deleteAttachment(a: TicketAttachment): Promise<void> {
  const { error } = await supabase.from('it_ticket_attachments').delete().eq('id', a.id)
  if (error) throw new Error(`Supabase delete error [it_ticket_attachments]: ${error.message}`)
  await removeObject(ATTACHMENTS_CONTAINER, a.storagePath).catch(() => {})
}

// ---------------------------------------------------------------------------
// Audit history (00560) — reads the shared public.audit_log table (see that
// migration's header comment for why there's no ticketing-specific audit table).
// ---------------------------------------------------------------------------

function rowToAuditEntry(row: Record<string, unknown>): TicketAuditEntry {
  const payload = row.payload
  return {
    id: s(row.id),
    action: s(row.action),
    summary: s(row.summary),
    actor: sOrNull(row.actor),
    actorName: sOrNull(row.actor_name),
    payload: payload && typeof payload === 'object' && !Array.isArray(payload) ? (payload as Record<string, unknown>) : {},
    createdAt: s(row.created_at),
  }
}

export async function listTicketAudit(ticketId: string): Promise<TicketAuditEntry[]> {
  const { data, error } = await supabase
    .from('audit_log')
    .select('*')
    .eq('entity_type', 'it_ticket')
    .eq('entity_id', ticketId)
    .order('created_at', { ascending: false })
  if (error) throw new Error(`Supabase query error [audit_log]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToAuditEntry)
}

// Every ticket's audit rows, newest first, for the company-wide History page.
// RLS decides what comes back (a customer user only sees their own tickets'
// rows, 00586), so this never filters by customer itself.
export async function listAllTicketAudit(opts: { since?: string; limit: number }): Promise<TicketAuditEntry[]> {
  let q = supabase
    .from('audit_log')
    .select('*')
    .eq('kind', 'it_ticketing')
    .eq('entity_type', 'it_ticket')
  if (opts.since) q = q.gte('created_at', opts.since)
  const { data, error } = await q.order('created_at', { ascending: false }).limit(opts.limit)
  if (error) throw new Error(`Supabase query error [audit_log]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map((row) => ({ ...rowToAuditEntry(row), entityId: s(row.entity_id) }))
}

// --- Report cards + saved views (00561) ---------------------------------------
// No query engine here on purpose — tickets are the module's only reportable
// entity and are already fetched in full client-side (listTickets()), so a
// "report card" is just a stored recipe; see lib/reportCards.ts for the
// client-side computation that turns a recipe + the ticket list into numbers.

function rowToReportCard(row: Record<string, unknown>): TicketReportCard {
  return {
    id: s(row.id),
    orgId: sOrNull(row.org_id),
    title: s(row.title),
    kind: oneOf<ReportCardKind>(row.kind, REPORT_CARD_KINDS, 'kpi'),
    // A ticket field, approval status, or custom:<key> (see ReportGroupKey).
    groupByField: row.group_by_field == null ? null : isReportGroupKey(row.group_by_field) ? row.group_by_field : 'status',
    measure: oneOf<ReportCardMeasure>(row.measure, REPORT_CARD_MEASURES, 'count'),
    chartType: row.chart_type == null ? null : oneOf<ReportChartType>(row.chart_type, REPORT_CHART_TYPES, 'bar'),
    filterField: row.filter_field == null ? null : isReportGroupKey(row.filter_field) ? row.filter_field : 'status',
    filterValue: sOrNull(row.filter_value),
    createdBy: sOrNull(row.created_by),
    createdAt: s(row.created_at),
    updatedAt: s(row.updated_at),
  }
}

function reportCardPayload(input: Partial<TicketReportCardInput>): Record<string, unknown> {
  return compact({
    title: input.title,
    kind: input.kind,
    group_by_field: input.groupByField,
    measure: input.measure,
    chart_type: input.chartType,
    filter_field: input.filterField,
    filter_value: input.filterValue,
  })
}

export async function listReportCards(): Promise<TicketReportCard[]> {
  const { data, error } = await supabase
    .from('it_ticketing_report_cards')
    .select('*')
    .eq('org_id', itTicketingOrgId())
    .order('created_at', { ascending: true })
  if (error) throw new Error(`Supabase query error [it_ticketing_report_cards]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToReportCard)
}

export async function createReportCard(input: TicketReportCardInput, createdBy: string): Promise<TicketReportCard> {
  const row = { id: `it_card_${generateId()}`, org_id: itTicketingOrgId(), created_by: createdBy, ...reportCardPayload(input) }
  const { data, error } = await supabase.from('it_ticketing_report_cards').insert(row).select().single()
  if (error) throw new Error(`Supabase insert error [it_ticketing_report_cards]: ${error.message}`)
  return rowToReportCard(data as Record<string, unknown>)
}

export async function deleteReportCard(id: string): Promise<void> {
  const { error } = await supabase.from('it_ticketing_report_cards').delete().eq('id', id)
  if (error) throw new Error(`Supabase delete error [it_ticketing_report_cards]: ${error.message}`)
}

function rowToSavedView(row: Record<string, unknown>): TicketSavedView {
  const filters = row.filters
  return {
    id: s(row.id),
    orgId: sOrNull(row.org_id),
    ownerId: sOrNull(row.owner_id),
    name: s(row.name),
    filters: (filters && typeof filters === 'object' && !Array.isArray(filters) ? filters : {}) as TicketSavedViewFilters,
    shared: row.shared === true,
    createdAt: s(row.created_at),
  }
}

export async function listSavedViews(): Promise<TicketSavedView[]> {
  const { data, error } = await supabase
    .from('it_ticketing_saved_views')
    .select('*')
    .eq('org_id', itTicketingOrgId())
    .order('created_at', { ascending: true })
  if (error) throw new Error(`Supabase query error [it_ticketing_saved_views]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToSavedView)
}

export async function createSavedView(input: TicketSavedViewInput, ownerId: string): Promise<TicketSavedView> {
  const row = compact({
    id: `it_view_${generateId()}`,
    org_id: itTicketingOrgId(),
    owner_id: ownerId,
    name: input.name,
    filters: input.filters,
    shared: input.shared,
  })
  const { data, error } = await supabase.from('it_ticketing_saved_views').insert(row).select().single()
  if (error) throw new Error(`Supabase insert error [it_ticketing_saved_views]: ${error.message}`)
  return rowToSavedView(data as Record<string, unknown>)
}

export async function deleteSavedView(id: string): Promise<void> {
  const { error } = await supabase.from('it_ticketing_saved_views').delete().eq('id', id)
  if (error) throw new Error(`Supabase delete error [it_ticketing_saved_views]: ${error.message}`)
}

// ---------------------------------------------------------------------------
// Ticket categories (00574) — admin-editable, replacing the hardcoded list.
// Writes need the manage_categories permission (enforced by RLS / the RPC).
// ---------------------------------------------------------------------------

function rowToCategory(row: Record<string, unknown>): TicketCategory {
  return {
    id: s(row.id),
    name: s(row.name),
    active: row.active !== false,
    seq: typeof row.seq === 'number' ? row.seq : 0,
  }
}

export async function listTicketCategories(opts?: { includeInactive?: boolean }): Promise<TicketCategory[]> {
  let q = supabase.from('it_ticket_categories').select('*').order('seq', { ascending: true }).order('name', { ascending: true })
  if (!opts?.includeInactive) q = q.eq('active', true)
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [it_ticket_categories]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToCategory)
}

export async function createTicketCategory(name: string, seq: number): Promise<TicketCategory> {
  const row = { id: `itc_${generateId()}`, name: name.trim(), active: true, seq }
  const { data, error } = await supabase.from('it_ticket_categories').insert(row).select().single()
  if (error) {
    if (error.code === '23505') throw new Error(`A category called "${name.trim()}" already exists.`)
    throw new Error(`Supabase insert error [it_ticket_categories]: ${error.message}`)
  }
  return rowToCategory(data as Record<string, unknown>)
}

/** Renames the category AND every ticket / category workflow rule using the old name. Returns tickets updated. */
export async function renameTicketCategory(id: string, name: string): Promise<number> {
  const { data, error } = await supabase.rpc('it_ticketing_rename_category', { p_id: id, p_name: name.trim() })
  if (error) {
    if (error.code === '23505') throw new Error(`A category called "${name.trim()}" already exists.`)
    throw new Error(error.message)
  }
  return typeof data === 'number' ? data : 0
}

export async function setTicketCategoryActive(id: string, active: boolean): Promise<void> {
  const { error } = await supabase.from('it_ticket_categories').update({ active }).eq('id', id)
  if (error) throw new Error(`Supabase update error [it_ticket_categories]: ${error.message}`)
}

/** Persist a new display order (seq = position). */
export async function reorderTicketCategories(orderedIds: string[]): Promise<void> {
  for (let i = 0; i < orderedIds.length; i++) {
    const { error } = await supabase.from('it_ticket_categories').update({ seq: i + 1 }).eq('id', orderedIds[i]!)
    if (error) throw new Error(`Supabase update error [it_ticket_categories]: ${error.message}`)
  }
}

// ---------------------------------------------------------------------------
// Ticket tasks (00569) — checklist items on a ticket. Visibility follows the
// parent ticket (RLS); org_id and the completion stamp are set server-side.
// ---------------------------------------------------------------------------

function rowToTask(row: Record<string, unknown>): TicketTask {
  return {
    id: s(row.id),
    orgId: sOrNull(row.org_id),
    ticketId: s(row.ticket_id),
    title: s(row.title),
    assigneeId: sOrNull(row.assignee_id),
    dueDate: sOrNull(row.due_date),
    done: row.done === true,
    completedAt: sOrNull(row.completed_at),
    completedBy: sOrNull(row.completed_by),
    seq: typeof row.seq === 'number' ? row.seq : 0,
    createdBy: sOrNull(row.created_by),
    createdAt: s(row.created_at),
  }
}

function taskPayload(input: Partial<TicketTaskInput>): Record<string, unknown> {
  const row: Record<string, unknown> = {}
  if (input.title !== undefined) row.title = input.title.trim()
  if (input.assigneeId !== undefined) row.assignee_id = input.assigneeId || null
  if (input.dueDate !== undefined) row.due_date = input.dueDate || null
  if (input.done !== undefined) row.done = input.done
  if (input.seq !== undefined) row.seq = input.seq
  return row
}

export async function listTicketTasks(ticketId: string): Promise<TicketTask[]> {
  const { data, error } = await supabase
    .from('it_ticket_tasks')
    .select('*')
    .eq('ticket_id', ticketId)
    .order('seq', { ascending: true })
    .order('created_at', { ascending: true })
  if (error) throw new Error(`Supabase query error [it_ticket_tasks]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToTask)
}

// For the queue's Tasks column / "My open tasks": every task on the given
// tickets. Chunked so a large queue doesn't overflow the request URL.
export async function listTasksForTickets(ticketIds: string[]): Promise<TicketTask[]> {
  const out: TicketTask[] = []
  for (let i = 0; i < ticketIds.length; i += 150) {
    const { data, error } = await supabase
      .from('it_ticket_tasks')
      .select('*')
      .in('ticket_id', ticketIds.slice(i, i + 150))
    if (error) throw new Error(`Supabase query error [it_ticket_tasks]: ${error.message}`)
    out.push(...((data ?? []) as Record<string, unknown>[]).map(rowToTask))
  }
  return out
}

export async function createTicketTask(ticketId: string, input: TicketTaskInput, createdBy: string | null): Promise<TicketTask> {
  const row = { id: `it_task_${generateId()}`, ticket_id: ticketId, created_by: createdBy, ...taskPayload(input) }
  const { data, error } = await supabase.from('it_ticket_tasks').insert(row).select().single()
  if (error) throw new Error(`Supabase insert error [it_ticket_tasks]: ${error.message}`)
  return rowToTask(data as Record<string, unknown>)
}

export async function updateTicketTask(id: string, patch: Partial<TicketTaskInput>): Promise<TicketTask> {
  const { data, error } = await supabase.from('it_ticket_tasks').update(taskPayload(patch)).eq('id', id).select().single()
  if (error) throw new Error(`Supabase update error [it_ticket_tasks]: ${error.message}`)
  return rowToTask(data as Record<string, unknown>)
}

export async function deleteTicketTask(id: string): Promise<void> {
  const { error } = await supabase.from('it_ticket_tasks').delete().eq('id', id)
  if (error) throw new Error(`Supabase delete error [it_ticket_tasks]: ${error.message}`)
}

// ---------------------------------------------------------------------------
// Ticket numbering (00547 / 00580) — prefix, padding and next number per
// customer. Rows are created lazily by it_ticketing_claim_ticket_number() with
// id 'itn_' || org_id; saving uses the same id so it updates that row. Writes
// need the manage_ticket_numbering permission (RLS).
// ---------------------------------------------------------------------------

const numberSettingsId = (orgId: string | null) => `itn_${orgId ?? 'default'}`

export async function listTicketNumberSettings(): Promise<TicketNumberSettings[]> {
  const { data, error } = await supabase.from('it_ticket_number_settings').select('org_id, prefix, padding, next_number')
  if (error) throw new Error(`Supabase query error [it_ticket_number_settings]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    orgId: sOrNull(r.org_id),
    prefix: s(r.prefix) || 'TICK-',
    padding: typeof r.padding === 'number' ? r.padding : 5,
    nextNumber: typeof r.next_number === 'number' ? r.next_number : 1,
  }))
}

export async function saveTicketNumberSettings(settings: TicketNumberSettings): Promise<void> {
  const row = {
    id: numberSettingsId(settings.orgId),
    org_id: settings.orgId,
    prefix: settings.prefix,
    padding: settings.padding,
    next_number: settings.nextNumber,
  }
  const { error } = await supabase.from('it_ticket_number_settings').upsert(row, { onConflict: 'id' })
  if (error) throw new Error(`Supabase save error [it_ticket_number_settings]: ${error.message}`)
}

/** The highest ticket number already issued with this prefix (visible to the user), or null. */
export async function latestTicketNumberWithPrefix(prefix: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('it_tickets')
    .select('ticket_number')
    // Escape LIKE wildcards in the prefix so "GT_" matches literally.
    .like('ticket_number', `${prefix.replace(/[\\%_]/g, (c) => '\\' + c)}%`)
    .order('ticket_number', { ascending: false })
    .limit(1)
  if (error) return null
  return ((data ?? [])[0] as { ticket_number?: string } | undefined)?.ticket_number ?? null
}

// ---------------------------------------------------------------------------
// Customer users (00586): a user tied to a customer sees that customer's whole
// ticket queue and nothing from other customers. Managed from Settings >
// Customers here and from Configuration > User Management (same table).
// Writes need the ticketing manage_users permission (admins by default).
// ---------------------------------------------------------------------------

function rowToMember(r: Record<string, unknown>): TicketingCustomerMember {
  return { id: s(r.id), customerId: s(r.customer_id), userId: s(r.user_id), active: r.active !== false }
}

/** Memberships for one customer, or for one user (pass exactly one). */
export async function listCustomerMembers(filter: { customerId?: string; userId?: string }): Promise<TicketingCustomerMember[]> {
  let q = supabase.from('it_ticketing_customer_members').select('*').order('created_at', { ascending: true })
  if (filter.customerId) q = q.eq('customer_id', filter.customerId)
  if (filter.userId) q = q.eq('user_id', filter.userId)
  const { data, error } = await q
  if (error) throw new Error(`Supabase query error [it_ticketing_customer_members]: ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(rowToMember)
}

/** Customers the user is an active member of. */
export async function listMyCustomerMemberOrgIds(currentUserId: string): Promise<string[]> {
  return (await listCustomerMembers({ userId: currentUserId })).filter((m) => m.active).map((m) => m.customerId)
}

/** Tie a user to a customer (re-activates an existing, inactive membership). */
export async function addCustomerMember(customerId: string, userId: string): Promise<void> {
  const existing = (await listCustomerMembers({ customerId })).find((m) => m.userId === userId)
  if (existing) {
    if (!existing.active) await setCustomerMemberActive(existing.id, true)
    return
  }
  const row = { id: `itcm_${generateId()}`, customer_id: customerId, user_id: userId, active: true }
  const { error } = await supabase.from('it_ticketing_customer_members').insert(row)
  if (error) throw new Error(`Supabase insert error [it_ticketing_customer_members]: ${error.message}`)
}

export async function setCustomerMemberActive(id: string, active: boolean): Promise<void> {
  const { error } = await supabase.from('it_ticketing_customer_members').update({ active }).eq('id', id)
  if (error) throw new Error(`Supabase update error [it_ticketing_customer_members]: ${error.message}`)
}
